import { useChartContext } from '../../ChartContext';
import {
  AssignmentLabel,
  AssignmentRowWrapper,
} from '../CentralChartSettingUI';
import { ICentralYAxisSettings, YAxisPosition } from '../../interfaces';
import styled from 'styled-components';
import { NumberInputField } from '../../../Form';
import { useAppDispatch } from '../../../../redux/hooks';
import { setChartSettingYAxisConfigAction } from '../../../../redux/slices/analyses';
import { isZodFieldErrors, zodFieldErrors } from '../../../../utils/zod';
import { MIN_INTEGER } from '../../../../config/constants';
import SegmentSelect from '../../../SegmentSelect';
import { validationSchema } from './validations';
import { BOUND_TYPE_OPTIONS } from './utils';
import { AxisBoundType } from './types';
import { useCentralChartSettingContext } from '../contexts/CentralChartSettingContext';
interface IProps {
  yAxisPosition: YAxisPosition;
}

const BoundsInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const StyledNumberInputField = styled(NumberInputField)<{
  $hasExtraPaddingRightForError: boolean;
}>`
  input {
    min-height: 20px;
    padding: 2px;
    width: 100%;
    border-radius: 2px;
    text-align: center;
    padding-right: ${({ $hasExtraPaddingRightForError }) =>
      $hasExtraPaddingRightForError ? '20px' : '0px'};
  }
`;

const YAxisBoundAssignment = ({ yAxisPosition }: IProps) => {
  const dispatch = useAppDispatch();

  const {
    metadata: { analysisId },
    settings: {
      chartSettings: { axisSettings },
    },
  } = useChartContext();

  const { customBound, setCustomBound } = useCentralChartSettingContext();
  const currentCustomBound = customBound[yAxisPosition];
  const currentYAxisSettings = axisSettings.yAxisSettings[yAxisPosition];

  const getCurrentBoundType = (): AxisBoundType => {
    if (currentCustomBound.isCustomApplied) {
      return AxisBoundType.Custom;
    }
    if (currentYAxisSettings.isTruncate) {
      return AxisBoundType.Truncate;
    }
    return AxisBoundType.Auto;
  };

  const setMinBound = (value: number | null) => {
    setCustomBound((draft) => {
      draft[yAxisPosition].min = value;
    });
    validateInputAndUpdateChartSettings(value, currentCustomBound.max);
  };

  const setMaxBound = (value: number | null) => {
    setCustomBound((draft) => {
      draft[yAxisPosition].max = value;
    });
    validateInputAndUpdateChartSettings(currentCustomBound.min, value);
  };

  const selectedBoundType = getCurrentBoundType();

  // will validate the min and max values before updating the chart settings
  const validateInputAndUpdateChartSettings = (
    newMin: number | null,
    newMax: number | null,
  ) => {
    const validationResult = validationSchema.safeParse({
      min: newMin,
      max: newMax,
    });
    setCustomBound((draft) => {
      draft[yAxisPosition].errors = validationResult.error ?? null;
    });

    if (validationResult.success) {
      dispatch(
        setChartSettingYAxisConfigAction({
          analysisId,
          yAxisPosition,
          config: {
            bounds: {
              min: newMin,
              max: newMax,
            },
          },
        }),
      );
    }
  };

  // if auto type, set truncate and bounds to false
  // if truncate type, set truncate to true and bounds to null
  // if custom type, set truncate to false and bounds to the min and max values
  const onBoundTypeChange = (value: AxisBoundType) => {
    const boundConfig: Pick<ICentralYAxisSettings, 'isTruncate' | 'bounds'> = {
      isTruncate: false,
      bounds: null,
    };

    if (value === AxisBoundType.Truncate) {
      boundConfig.isTruncate = true;
    }

    if (value === AxisBoundType.Custom) {
      boundConfig.bounds = {
        min: currentCustomBound.min,
        max: currentCustomBound.max,
      };
      setCustomBound((draft) => {
        draft[yAxisPosition].isCustomApplied = true;
      });
    } else {
      setCustomBound((draft) => {
        draft[yAxisPosition].isCustomApplied = false;
      });
    }

    dispatch(
      setChartSettingYAxisConfigAction({
        analysisId,
        yAxisPosition,
        config: boundConfig,
      }),
    );
  };

  return (
    <>
      <AssignmentRowWrapper>
        <AssignmentLabel>Bound</AssignmentLabel>
        <SegmentSelect
          options={BOUND_TYPE_OPTIONS}
          value={selectedBoundType}
          onChange={onBoundTypeChange}
        />
      </AssignmentRowWrapper>

      {selectedBoundType === AxisBoundType.Custom && (
        <AssignmentRowWrapper>
          <AssignmentLabel>Bounds</AssignmentLabel>
          <BoundsInputWrapper>
            <StyledNumberInputField
              value={currentCustomBound.min}
              min={MIN_INTEGER}
              onChange={(value) => setMinBound(value)}
              placeholder="Min"
              hasError={isZodFieldErrors(currentCustomBound.errors, 'min')}
              errorMessage={zodFieldErrors(currentCustomBound.errors, 'min')}
              $hasExtraPaddingRightForError={isZodFieldErrors(
                currentCustomBound.errors,
                'min',
              )}
            />

            <StyledNumberInputField
              value={currentCustomBound.max}
              min={MIN_INTEGER}
              onChange={(value) => setMaxBound(value)}
              placeholder="Max"
              hasError={isZodFieldErrors(currentCustomBound.errors, 'max')}
              errorMessage={zodFieldErrors(currentCustomBound.errors, 'max')}
              $hasExtraPaddingRightForError={isZodFieldErrors(
                currentCustomBound.errors,
                'max',
              )}
            />
          </BoundsInputWrapper>
        </AssignmentRowWrapper>
      )}
    </>
  );
};

export default YAxisBoundAssignment;
